import { KeyValue } from "@/main/models/common";
import {
	DamagePartAIResponse,
	PartSearchResponse,
	QuoteGetItemConditionsWrapper,
	QuoteProcurementPartInfoResponse,
} from "@/main/models/response";
import { getTranslateByFullKey, getTranslateByKey, isKeyTranslate } from "@/main/services/utils";

import { normalizeAiConfidence } from "../../utilsAi";
import { findDamageAreaByPosition, findDamageAreaOfPart } from "../damageArea/utilsDamageArea";
import { IAiVehicleResult, IVehicleManualForm } from "../vehicleData/types";
import {
	IDamagePart,
	IDamagePartsByConfidence,
	IPartsCatalogFilters,
	PartActionEnum,
	PartConfidenceEnum,
} from "./types";

export const INITIAL_CATALOG_FILTERS: IPartsCatalogFilters = {
	searchTerm: "",
	guidPartCategory: null,
	guidPartSubCategory: null,
	idsDamageArea: [],
};

export const AI_CONFIDENCE_HIGH_THRESHOLD = 70;

const PART_ID_SEPARATOR = "::";

const TRANSLATE_NOT_FOUND_VALUES = ["Translate not found", "Translates is empty"];

export function translatePartCategoryName(categoryName?: string | null) {
	if (!categoryName) return "";
	if (!isKeyTranslate(categoryName)) return categoryName;

	const translated = getTranslateByFullKey(categoryName);

	return TRANSLATE_NOT_FOUND_VALUES.includes(translated) ? "" : translated;
}

export function splitPartsByConfidence(parts: IDamagePart[]): IDamagePartsByConfidence {
	return parts.reduce(
		(acc, part) => {
			if (part.confidence === PartConfidenceEnum.High) acc.high.push(part);
			else acc.low.push(part);
			return acc;
		},
		{ high: [], low: [] } as IDamagePartsByConfidence,
	);
}

function toIdDamageArea(area?: KeyValue): number | null {
	const idDamageArea = Number(area?.key);

	return Number.isNaN(idDamageArea) ? null : idDamageArea;
}

export function isPartToReplace(part: IDamagePart) {
	return part.isDamaged && part.action === PartActionEnum.Replace;
}

export function countPartsToReplace(parts: IDamagePart[]) {
	return parts.filter(isPartToReplace).length;
}

export function buildPositionLabel(position: KeyValue) {
	const label = String(position.value ?? "");

	return normalizePartPosition(label) || label;
}

export function sortPositions(positions: KeyValue[]) {
	return [...positions].sort((positionA, positionB) => {
		const isNotAvailableA = !normalizePartPosition(positionA.value);
		const isNotAvailableB = !normalizePartPosition(positionB.value);

		if (isNotAvailableA !== isNotAvailableB) return isNotAvailableA ? -1 : 1;

		return buildPositionLabel(positionA).localeCompare(buildPositionLabel(positionB));
	});
}

export function buildSelectPositionLabel() {
	return getTranslateByKey("information.selectOne", true).replace(
		"{0}",
		getTranslateByKey("information.position").toLowerCase(),
	);
}

export function buildDamagePartId(guidPart: string, guidPosition: string) {
	return [guidPart, guidPosition].filter(Boolean).join(PART_ID_SEPARATOR);
}

export function buildSearchPartId(part: PartSearchResponse) {
	return buildDamagePartId(part.guid, part.guidPosition);
}

export function findPartByPosition(parts: IDamagePart[], guidPart: string, guidPosition: string) {
	return parts.find((part) => part.guidPart === guidPart && part.guidPosition === guidPosition);
}

export function findPartFromSearch(parts: IDamagePart[], part: PartSearchResponse) {
	const hasOwnPosition = Boolean(normalizePartPosition(part.position));

	return parts.find(
		(item) => item.guidPart === part.guid && (!hasOwnPosition || item.guidPosition === (part.guidPosition ?? "")),
	);
}

export function isSearchPartSelected(parts: IDamagePart[], part: PartSearchResponse) {
	return Boolean(findPartFromSearch(parts, part));
}

export function getPositionGuidsUsedByPart(parts: IDamagePart[], guidPart: string) {
	return parts
		.filter((part) => part.guidPart === guidPart && Boolean(part.guidPosition))
		.map((part) => part.guidPosition);
}

export function mapSearchResponseToDamagePart(
	part: PartSearchResponse,
	position: KeyValue | null,
	areasDomain: KeyValue[] = [],
): IDamagePart {
	const guidPosition = position ? String(position.key) : "";
	const areaFound = position ? findDamageAreaByPosition(String(position.value ?? ""), areasDomain) : undefined;

	return {
		id: buildDamagePartId(part.guid, guidPosition),
		guidPart: part.guid,
		guidQuoteItem: "",
		name: part.partName,
		categoryName: translatePartCategoryName(part.partCategoryName),
		subCategoryName: part.partSubCategoryName,
		guidPosition,
		position: normalizePartPosition(position?.value),
		idDamageArea: toIdDamageArea(areaFound),
		confidence: PartConfidenceEnum.High,
		isDamaged: true,
		action: PartActionEnum.Replace,
		isFromAi: false,
	};
}

export function mapAiResponseToDamageParts(parts: DamagePartAIResponse[], areasDomain: KeyValue[] = []): IDamagePart[] {
	return parts.map((part) => {
		const isHighConfidence = normalizeAiConfidence(part.aiConfidence) >= AI_CONFIDENCE_HIGH_THRESHOLD;

		const guidPosition = part.guidPosition ?? "";
		const areaFound = findDamageAreaOfPart(part, areasDomain);

		return {
			id: buildDamagePartId(part.guidPart, guidPosition),
			guidPart: part.guidPart,
			guidQuoteItem: "",
			name: part.partName,
			categoryName: translatePartCategoryName(part.partCategoryName),
			subCategoryName: part.partSubCategoryName,
			guidPosition,
			position: normalizePartPosition(part.position),
			idDamageArea: toIdDamageArea(areaFound),
			confidence: isHighConfidence ? PartConfidenceEnum.High : PartConfidenceEnum.Low,
			isDamaged: isHighConfidence,
			action: null,
			isFromAi: true,
		};
	});
}

function buildDamagePartFromQuoteItem(
	guidQuoteItem: string,
	guidPart: string,
	partInfo?: QuoteProcurementPartInfoResponse,
): IDamagePart {
	return {
		id: guidQuoteItem,
		guidPart,
		guidQuoteItem,
		name: partInfo?.partName || partInfo?.supplierInventoryPartTitle || "",
		categoryName: "",
		subCategoryName: "",
		guidPosition: "",
		position: normalizePartPosition(partInfo?.partPosition),
		idDamageArea: null,
		confidence: PartConfidenceEnum.High,
		isDamaged: true,
		action: PartActionEnum.Replace,
		isFromAi: false,
	};
}

export function mapQuoteItemsToDamageParts(result: QuoteGetItemConditionsWrapper): IDamagePart[] {
	if (!result) return [];

	const assignedParts = (result.assignedItems ?? []).flatMap((group) =>
		(group.quoteSupplierItems ?? []).map((item) =>
			buildDamagePartFromQuoteItem(
				item.quoteItem?.guid ?? "",
				item.quoteItem?.guidPart ?? "",
				item.quoteItem?.quoteProcurementPartInfo,
			),
		),
	);

	const pendingParts = [
		result.unassignedItems,
		result.unansweredItems,
		result.unansweredCanceledItems,
		result.unansweredRequotedItems,
	].flatMap((condition) =>
		(condition?.parts ?? []).map((item) =>
			buildDamagePartFromQuoteItem(item.guidQuoteItem ?? "", item.guidPart ?? "", item.quoteProcurementPartInfo),
		),
	);

	return [...assignedParts, ...pendingParts].filter((part) => Boolean(part.guidQuoteItem));
}

export function attachQuoteItemsToParts(parts: IDamagePart[], partsOnQuote: IDamagePart[]): IDamagePart[] {
	const partsNotMatched = [...partsOnQuote];

	const partsAttached = parts.map((part) => {
		if (!isPartToReplace(part)) return { ...part, guidQuoteItem: "" };

		const indexOnQuote = partsNotMatched.findIndex((item) => item.guidPart === part.guidPart);

		if (indexOnQuote < 0) return { ...part, guidQuoteItem: "" };

		const [partOnQuote] = partsNotMatched.splice(indexOnQuote, 1);

		return { ...part, guidQuoteItem: partOnQuote.guidQuoteItem };
	});

	return [...partsAttached, ...partsNotMatched];
}

const POSITION_NOT_AVAILABLE_VALUES = ["n/a", "na", "n.a.", "n/d", "nd"];

const POSITION_PIES_ID_PREFIX = /^\s*\d+\s*[-\u2013\u2014]\s*/;

export function normalizePartPosition(position?: string | null) {
	const value = (position ?? "").trim();
	if (!value) return "";

	const label = isKeyTranslate(value) ? getTranslateByFullKey(value) : value;
	if (TRANSLATE_NOT_FOUND_VALUES.includes(label)) return "";

	const labelWithoutPiesId = label.replace(POSITION_PIES_ID_PREFIX, "").trim();
	if (POSITION_NOT_AVAILABLE_VALUES.includes(labelWithoutPiesId.toLowerCase())) return "";

	return labelWithoutPiesId;
}

export function buildPartDescription(categoryName: string, subCategoryName: string) {
	return [categoryName, subCategoryName].filter(Boolean).join(" · ");
}

export function buildPartName(name: string, position: string) {
	return [name, position].filter(Boolean).join(" - ");
}

export function buildVehicleLabel(aiResult: IAiVehicleResult | null, manualForm: IVehicleManualForm | null) {
	if (aiResult?.name) return aiResult.name;
	if (!manualForm) return "";

	return [manualForm.manufacturer, manualForm.model, manualForm.trim, manualForm.year]
		.filter(Boolean)
		.map((item) => item?.value)
		.join(" ");
}

export function buildPartsCatalogSubtitle(vehicleLabel: string) {
	return getTranslateByKey("information.partsCatalogSubtitle").replace("{0}", vehicleLabel);
}

export function buildPartsSubtitle(isAiMode: boolean) {
	return getTranslateByKey(isAiMode ? "information.damagePartsSubtitleAi" : "information.damagePartsSubtitleManual");
}

export function buildAddPartLabel(hasParts: boolean) {
	return getTranslateByKey(hasParts ? "information.addAnotherPart" : "information.searchPartsInCatalog");
}

const COUNT_PLACEHOLDER = "{0}";
const COUNT_PREFIX_SEPARATORS = /[\s([{·|,:;\-–—]+$/;

function removeCountFromLabel(label: string) {
	const placeholderIndex = label.indexOf(COUNT_PLACEHOLDER);
	if (placeholderIndex < 0) return label;

	const labelWithoutCount = label.slice(0, placeholderIndex).replace(COUNT_PREFIX_SEPARATORS, "");

	return labelWithoutCount || label.replace(COUNT_PLACEHOLDER, "").trim();
}

export function buildNextButtonLabel(countToReplace: number) {
	const label = getTranslateByKey("information.searchPricesWithCount");

	if (countToReplace === 0) return removeCountFromLabel(label);

	return label.replace(COUNT_PLACEHOLDER, String(countToReplace));
}

export function buildPartsFoundLabel(total: number) {
	return getTranslateByKey("information.partsFoundCount").replace("{0}", String(total));
}

export function hasActiveFilters(filters: IPartsCatalogFilters) {
	return !!filters.guidPartCategory || !!filters.guidPartSubCategory || filters.idsDamageArea.length > 0;
}

export function buildFiltersLabel(
	filters: IPartsCatalogFilters,
	categoryName: string | null,
	subCategoryName: string | null,
	damageAreaNames: string[],
) {
	const labels = [subCategoryName ?? categoryName, ...damageAreaNames].filter(Boolean) as string[];

	return labels.length ? labels.join(" · ") : getTranslateByKey("information.filters");
}
